// src/manager/pages/RegisterUser.tsx — name+password only, shows login_id
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Copy, CheckCircle } from 'lucide-react';
import { useManager } from '../hooks/useManager';
import { copyText } from '../../shared/clipboard';

interface FormData { name: string; login_id?: string; password: string; initialCredits: number; }

export default function RegisterUser() {
  const { registerUser, fetchUsers, fetchAccount } = useManager();
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>({
    defaultValues: { initialCredits: 0 },
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [createdLoginId, setCreatedLoginId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [accountCredits, setAccountCredits] = useState<number>(0);

  useEffect(() => {
    fetchAccount()
      .then((account) => setAccountCredits(Number(account.credits || 0)))
      .catch(() => {});
  }, [fetchAccount]);

  const copyToClipboard = async (text: string) => {
    try {
      await copyText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Copy failed. Select and copy manually.');
    }
  };

  const onSubmit = async (data: FormData) => {
    setError(''); setLoading(true); setCreatedLoginId(null);
    try {
      const result = await registerUser({
        name: data.name.trim(),
        login_id: data.login_id?.trim() || undefined,
        password: data.password,
        initialCredits: Number(data.initialCredits || 0),
      });
      const loginId = result?.user?.login_id || result?.user?.user?.login_id || result?.login_id;
      setCreatedLoginId(loginId || null);
      await fetchUsers();
      const account = await fetchAccount();
      setAccountCredits(Number(account.credits || 0));
      reset();
    } catch (err: any) {
      setError(err.message || 'Failed to register user');
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 max-w-xl mx-auto">
      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Register New User</h2>

      {createdLoginId && (
        <div className="mb-4 p-4 bg-green-50 border-2 border-green-400 rounded-xl">
          <p className="text-green-800 font-bold mb-2">✅ User Registered!</p>
          <div className="flex items-center gap-3 bg-white p-3 rounded-lg border border-green-300">
            <div>
              <p className="text-xs text-gray-500 mb-1">User Login ID</p>
              <p className="text-xl font-mono font-bold text-indigo-700 tracking-widest">{createdLoginId}</p>
            </div>
            <button onClick={() => copyToClipboard(createdLoginId)} className="ml-auto p-2 bg-indigo-100 hover:bg-indigo-200 rounded-lg">
              {copied ? <CheckCircle size={18} className="text-green-600" /> : <Copy size={18} className="text-indigo-600" />}
            </button>
          </div>
          <p className="mt-2 text-xs text-green-700">Share this ID and password with the player. They use it to login.</p>
        </div>
      )}

      <div className="mb-4 rounded-xl border border-purple-200 bg-purple-50 p-4">
        <p className="text-sm text-purple-700">Available Manager Credits</p>
        <p className="text-2xl font-bold text-purple-900">{accountCredits.toFixed(0)}</p>
      </div>

      <div className="bg-white shadow rounded-xl p-5">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Player Name</label>
            <input placeholder="Full Name"
              {...register('name', { required: 'Name required', pattern: { value: /^[A-Za-z\s]{2,50}$/, message: '2-50 letters only' } })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600" />
            {errors.name && <p className="text-red-600 text-sm">{errors.name.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User ID (Optional)</label>
            <input placeholder="Leave blank to auto-generate"
              {...register('login_id', { pattern: { value: /^[A-Za-z0-9_-]{3,20}$/, message: '3-20 letters, numbers, _ or -' } })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600" />
            {errors.login_id && <p className="text-red-600 text-sm">{errors.login_id.message}</p>}
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input type={showPassword ? 'text' : 'password'} placeholder="At least 8 characters"
              {...register('password', { required: 'Password required', minLength: { value: 8, message: 'Min 8 characters' } })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg pr-12 focus:ring-2 focus:ring-purple-600" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-9 text-gray-600">
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
            {errors.password && <p className="text-red-600 text-sm">{errors.password.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Initial Credits</label>
            <input
              type="number"
              min="0"
              step="1"
              placeholder="0"
              {...register('initialCredits', {
                valueAsNumber: true,
                min: { value: 0, message: 'Credits cannot be negative' },
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
            />
            {errors.initialCredits && <p className="text-red-600 text-sm">{errors.initialCredits.message}</p>}
            <p className="mt-1 text-xs text-gray-500">This amount will be deducted from your manager balance.</p>
          </div>

          {error && <p className="text-red-600 text-sm text-center">{error}</p>}

          <button type="submit" disabled={loading}
            className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-70 transition">
            {loading ? 'Registering...' : 'Register User'}
          </button>
        </form>
      </div>
      <p className="mt-3 text-xs text-gray-400 text-center">Enter a User ID to use it as Login ID, or leave blank to auto-generate USR######.</p>
    </div>
  );
}
